// The steps of the match and the cursor on them. A null cursor is the live
// game. Any other value shows an earlier step on the board. The game goes on
// only when the cursor is null: the director holds its beats and the AI
// driver waits.

import {
  firstStep,
  type HistoryStep,
  resolveCursor,
  stepBefore,
  stepBetween,
  stepWithLogEntry,
} from '../game/history';
import type { GameState } from '../game/state';
import { createStore } from '../lib/store';

export type HistoryState = {
  steps: readonly HistoryStep[];
  /** The step on the board. Null is the live game. */
  cursor: number | null;
  /** The live game changed after the last step with an action that made no step. */
  liveIsAhead: boolean;
};

export const historyStore = createStore<HistoryState>({
  steps: [],
  cursor: null,
  liveIsAhead: false,
});

export const resetHistory = (start: GameState): void =>
  historyStore.set({ steps: [firstStep(start)], cursor: null, liveIsAhead: false });

/** Puts back the steps of a match that was replayed from a save. The cursor is live. */
export const restoreHistory = (steps: readonly HistoryStep[], liveIsAhead: boolean): void =>
  historyStore.set({ steps, cursor: null, liveIsAhead });

/** Call after every action on the game store. */
export const recordAction = (before: GameState, after: GameState): void => {
  if (after === before) return;
  const step = stepBetween(before, after);
  historyStore.set((history) =>
    step
      ? { ...history, steps: [...history.steps, step], liveIsAhead: false }
      : { ...history, liveIsAhead: true },
  );
};

export const reviewedStep = (): HistoryStep | null => {
  const { steps, cursor } = historyStore.get();
  return cursor === null ? null : (steps[cursor] ?? null);
};

export const isReviewing = (): boolean => historyStore.get().cursor !== null;

export const goToStep = (requested: number): void =>
  historyStore.set((history) => {
    const cursor = resolveCursor(requested, history.steps.length, history.liveIsAhead);
    return cursor === history.cursor ? history : { ...history, cursor };
  });

export const goToStart = (): void => goToStep(0);

export const goLive = (): void => goToStep(historyStore.get().steps.length);

export const stepBack = (): void => {
  const { cursor, steps, liveIsAhead } = historyStore.get();
  goToStep(stepBefore(cursor, steps.length, liveIsAhead));
};

export const stepForward = (): void => {
  const { cursor } = historyStore.get();
  if (cursor !== null) goToStep(cursor + 1);
};

export const goToLogEntry = (logId: number): void =>
  goToStep(stepWithLogEntry(historyStore.get().steps, logId));
